import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, KeyRound, CheckCircle2 } from "lucide-react";
import { useNoIndex, usePortalAuth, extractErr } from "@/portal/PortalAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/portal`;
const inp = "w-full rounded-lg bg-white border border-[#E7E5E4] focus:outline-none focus:ring-1 focus:ring-[#8A9A86] focus:border-[#8A9A86] h-12 px-4 text-[#1C1917]";
const btn = "w-full rounded-full bg-[#1C1917] text-white h-12 font-medium text-sm hover:bg-[#44403C] transition-colors disabled:opacity-60";

const ROLE_DEST = {
  guest: "/guest/dashboard",
  homeowner: "/homeowner/dashboard",
  admin: "/management/dashboard",
  manager: "/management/dashboard",
  reservations: "/management/dashboard",
  housekeeping: "/management/dashboard",
  maintenance: "/management/dashboard",
};

export default function PortalActivate() {
  useNoIndex();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [status, setStatus] = useState("checking"); // checking | valid | invalid | done
  const [error, setError] = useState("");
  const [invite, setInvite] = useState(null);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const { refresh } = usePortalAuth();

  useEffect(() => {
    if (!token) { setStatus("invalid"); setError("Missing invitation token."); return; }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/invitation/${encodeURIComponent(token)}`);
        setInvite(data);
        setStatus("valid");
      } catch (e) {
        setStatus("invalid");
        setError(extractErr(e));
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (pw !== pw2) { setError("Passwords do not match."); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/activate`, { token, new_password: pw });
      localStorage.setItem("masaya_portal_token", data.token);
      await refresh();
      setStatus("done");
      toast.success("Account activated. Redirecting…");
      const dest = ROLE_DEST[data.user.role] || "/portal";
      setTimeout(() => navigate(dest, { replace: true }), 900);
    } catch (e) {
      setError(extractErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="portal-activate" className="px-6 md:px-12 pt-36 md:pt-40 pb-24 min-h-[80vh]">
      <div className="max-w-md mx-auto">
        <Link to="/portal" className="inline-flex items-center gap-2 text-sm text-[#44403C] hover:text-[#1C1917]">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> All portals
        </Link>
        <div className="mt-8 rounded-2xl border border-[#E7E5E4] bg-[#F5F5F4] p-8 md:p-10">
          <span className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-medium text-[#8A9A86]">
            <KeyRound className="w-3.5 h-3.5" /> Portal activation
          </span>
          {status === "checking" && (
            <p className="mt-6 text-[#78716C]" data-testid="activate-checking">Verifying your invitation…</p>
          )}
          {status === "invalid" && (
            <>
              <h1 className="mt-3 text-2xl font-normal tracking-tight text-[#1C1917]">Invitation not valid</h1>
              <p className="mt-2 text-sm text-[#44403C] font-light" data-testid="activate-error">{error || "Please ask an administrator to resend your invitation."}</p>
              <Link to="/portal" className="mt-6 inline-block text-sm text-[#8A9A86] hover:text-[#1C1917] underline underline-offset-4">Return to portal home</Link>
            </>
          )}
          {status === "valid" && invite && (
            <>
              <h1 className="mt-3 text-2xl font-normal tracking-tight text-[#1C1917]">Set your password</h1>
              <p className="mt-2 text-sm text-[#44403C] font-light">
                Welcome{invite.first_name ? `, ${invite.first_name}` : ""}. Set a password to activate your{" "}
                <span className="uppercase tracking-wider text-[#8A9A86]">{invite.role}</span> account for <strong className="font-medium text-[#1C1917]">{invite.email}</strong>.
              </p>
              <form onSubmit={submit} className="mt-6 space-y-4" data-testid="activate-form">
                <div>
                  <label className="text-sm text-[#1C1917] mb-1 block">New password (min 8 chars)</label>
                  <input type="password" required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} data-testid="activate-password" className={inp} />
                </div>
                <div>
                  <label className="text-sm text-[#1C1917] mb-1 block">Confirm password</label>
                  <input type="password" required minLength={8} value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="activate-password-confirm" className={inp} />
                </div>
                {error && <p className="text-sm text-red-600" data-testid="activate-error">{error}</p>}
                <button type="submit" disabled={busy} data-testid="activate-submit" className={btn}>
                  {busy ? "Activating…" : "Activate my account"}
                </button>
              </form>
            </>
          )}
          {status === "done" && (
            <div className="mt-6 text-center" data-testid="activate-success">
              <CheckCircle2 className="w-10 h-10 mx-auto text-[#8A9A86]" strokeWidth={1.5} />
              <p className="mt-4 text-lg font-normal text-[#1C1917]">Account activated</p>
              <p className="mt-1 text-sm text-[#78716C]">Redirecting you to your portal…</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
